import React, { useEffect, useRef } from 'react';
import { ApiErrorResponse } from '~/util/types/api';
import { useAuthData } from './AuthProvider';
import { toast } from 'react-toastify';
import { APIContext } from './APIContext';
import type { APIContextType } from './APIContext';
import { api, ApiRequestWithToast, setApiRequestContext } from './APIClient';

// API PROVIDER (for use in components with extra toasts helper)

const sendWithToast = async function <T = unknown>(
    data: ApiRequestWithToast<T>,
) {
    const toastId = toast.loading(data?.loadingMessage ?? 'Loading...', {
        autoClose: 10000,
    });

    const response = await api.send<T>(data.requestData);
    if (response.error) {
        toast.update(toastId, {
            render:
                typeof data?.errorMessage === 'function'
                    ? data.errorMessage(response as ApiErrorResponse)
                    : (data?.errorMessage ?? 'Failed to process request:') +
                      (response.data.message ?? 'Unknown error'),
            type: 'error',
            isLoading: false,
            autoClose: 10000,
        });
        data?.errorCallback?.(response);
    } else {
        toast.update(toastId, {
            render:
                typeof data?.successMessage === 'function'
                    ? data.successMessage(response)
                    : (data?.successMessage ?? 'Success'),
            type: 'success',
            isLoading: false,
            autoClose: 5000,
        });
        data?.successCallback?.(response);
    }
    return response;
};

const value: APIContextType = { api: { ...api, sendWithToast } };

export const APIProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { access_token, scopes = [] } = useAuthData() ?? {};

    const tokenRef = useRef<string | undefined>(access_token);
    const hasStaffScopesRef = useRef<boolean>(scopes.length > 0);

    useEffect(() => {
        tokenRef.current = access_token ?? undefined;
        hasStaffScopesRef.current = scopes.length > 0;
    }, [access_token, scopes]);

    useEffect(() => {
        setApiRequestContext({
            token: tokenRef.current,
            hasStaffScope: hasStaffScopesRef.current,
        });
    }, [access_token, scopes.length]);

    return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
};
